import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Clock, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Logo } from "../components/Logo";
import { ORG_QUESTIONS, ORG_SCALE, ORG_RESULTS, EMPLOYEE_OPTIONS } from "../data/orgAssessment";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.4, ease: "easeOut" },
};

const Kicker = ({ children }) => (
  <p className="text-[11px] sm:text-xs font-bold tracking-[0.25em] text-gold uppercase">{children}</p>
);

const GoldButton = ({ children, disabled, onClick, type = "button", testId }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 px-8 py-3.5 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase transition-[background-color,box-shadow,opacity] duration-300 ${
      disabled
        ? "bg-gold/25 text-black/50 cursor-not-allowed"
        : "bg-gold text-black hover:bg-gold-hover hover:shadow-[0_0_25px_-5px_rgba(212,175,55,0.6)]"
    }`}
  >
    {children}
  </button>
);

const Footer = () => (
  <p className="mt-14 text-center text-[10px] tracking-[0.25em] text-white/30 uppercase">
    CC-ELEVATE.COM &nbsp;&middot;&nbsp; &copy; 2026 CC Elevate
  </p>
);

const inputCls =
  "w-full bg-[#111] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors duration-300";

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-[11px] font-bold tracking-[0.15em] uppercase text-white/60 mb-2">{label}</label>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);

export default function OrgAssessment() {
  const [step, setStep] = useState("intro"); // intro | 0..19 | form | result
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({
    full_name: "",
    role: "",
    company: "",
    email: "",
    employees: "",
    phone: "",
    phone_prefix: "+39",
    privacy_accepted: false,
    marketing_accepted: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [apiError, setApiError] = useState("");

  const total = ORG_QUESTIONS.length;

  const selectAnswer = (qid, value) => setAnswers((prev) => ({ ...prev, [qid]: value }));

  const goNext = () => {
    if (typeof step === "number") {
      if (step < total - 1) setStep(step + 1);
      else setStep("form");
    }
  };
  const goBack = () => {
    if (typeof step === "number") {
      if (step === 0) setStep("intro");
      else setStep(step - 1);
    }
  };

  const restart = () => {
    setAnswers({});
    setForm({ full_name: "", role: "", company: "", email: "", employees: "", phone: "", phone_prefix: "+39", privacy_accepted: false, marketing_accepted: false });
    setErrors({});
    setResult(null);
    setStep("intro");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validateForm = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Campo obbligatorio";
    if (!form.role.trim()) e.role = "Campo obbligatorio";
    if (!form.company.trim()) e.company = "Campo obbligatorio";
    if (!form.email.trim()) e.email = "Campo obbligatorio";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email non valida";
    if (!form.employees) e.employees = "Seleziona un'opzione";
    if (!form.privacy_accepted) e.privacy = "Devi accettare la Privacy Policy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitForm = async (ev) => {
    ev.preventDefault();
    setApiError("");
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/org-leads`, { ...form, answers });
      setResult(data);
      setStep("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setApiError(err?.response?.data?.detail || "Si è verificato un errore. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = typeof step === "number" ? ORG_QUESTIONS[step] : null;
  const progress = typeof step === "number" ? Math.round(((step + 1) / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center px-4 py-10 sm:py-14">
      <div className="w-full max-w-2xl flex-1 flex flex-col">
        <Logo className="h-24 sm:h-28 mb-10 sm:mb-14" />

        <AnimatePresence mode="wait">
          {/* INTRO */}
          {step === "intro" && (
            <motion.div key="intro" {...fade} className="flex-1">
              <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-6 sm:px-12 py-12 sm:py-16 text-center">
                <Kicker>Assessment Organizzativo</Kicker>
                <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-white leading-tight">
                  Lo stato di salute della tua <em className="text-gold italic font-normal">organizzazione</em>
                </h1>
                <p className="mt-6 text-gray-400 text-sm sm:text-base leading-relaxed max-w-lg mx-auto">
                  20 domande su cinque dimensioni per fotografare la maturità organizzativa della tua azienda e ricevere una diagnosi con consiglio strategico.
                </p>
                <div className="mt-6 flex items-center justify-center gap-2 text-gold text-xs sm:text-sm font-semibold tracking-wide">
                  <Clock className="h-4 w-4" />
                  <span>circa 15 minuti &middot; 20 domande &middot; Diagnosi immediata</span>
                </div>
                <div className="mt-9">
                  <GoldButton onClick={() => setStep(0)} testId="org-start-btn">
                    Inizia l'Assessment <ArrowRight className="h-4 w-4" />
                  </GoldButton>
                </div>
              </div>
            </motion.div>
          )}

          {/* QUESTIONS */}
          {currentQuestion && (
            <motion.div key={currentQuestion.id} {...fade} className="flex-1">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[11px] sm:text-xs font-bold tracking-[0.2em] text-gold uppercase" data-testid="org-progress-label">
                  Domanda {step + 1} di {total}
                </span>
                <span className="text-[11px] sm:text-xs font-bold tracking-[0.1em] text-white/60">{progress}%</span>
              </div>
              <div className="h-[3px] w-full bg-white/10 mb-8">
                <div className="h-full bg-gold transition-all duration-500 ease-out" style={{ width: `${progress}%` }} data-testid="org-progress-bar" />
              </div>

              <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-5 sm:px-10 py-9 sm:py-11">
                <div className="pb-5 mb-6 border-b border-gold/25">
                  <Kicker>{currentQuestion.sectionTitle}</Kicker>
                </div>
                <p className="text-[11px] tracking-[0.15em] uppercase text-gold/70 font-semibold">
                  Affermazione {currentQuestion.number} &middot; {total}
                </p>
                <h2 className="mt-4 font-serif text-2xl sm:text-3xl leading-snug text-white">
                  {currentQuestion.text}
                </h2>

                <div className="mt-8">
                  <div className="flex items-center justify-between gap-2 sm:gap-3">
                    {ORG_SCALE.map((s) => {
                      const sel = answers[currentQuestion.id] === s.value;
                      return (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => selectAnswer(currentQuestion.id, s.value)}
                          data-testid={`org-scale-${s.value}`}
                          className={`flex-1 aspect-square max-w-[76px] flex items-center justify-center border text-lg font-bold transition-[transform,border-color,background-color,color] duration-300 hover:-translate-y-0.5 ${
                            sel ? "border-gold bg-gold text-black" : "border-white/20 bg-[#0d0d0d] text-white/70 hover:border-gold/60"
                          }`}
                        >
                          {s.value}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[11px] tracking-wide text-white/40 uppercase">
                    <span>1 &middot; Molto marginale</span>
                    <span>5 &middot; Molto solido</span>
                  </div>
                </div>

                <div className="mt-9 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={goBack}
                    data-testid="org-back-btn"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase text-white/60 hover:text-gold transition-colors duration-300"
                  >
                    <ArrowLeft className="h-4 w-4" /> Indietro
                  </button>
                  <GoldButton onClick={goNext} disabled={!answers[currentQuestion.id]} testId="org-next-btn">
                    Avanti <ArrowRight className="h-4 w-4" />
                  </GoldButton>
                </div>
              </div>
            </motion.div>
          )}

          {/* LEAD FORM */}
          {step === "form" && (
            <motion.div key="form" {...fade} className="flex-1">
              <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-6 sm:px-10 py-11">
                <Kicker>Ultimo passo</Kicker>
                <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-white">
                  Dove invio la tua <em className="text-gold italic font-normal">diagnosi</em> organizzativa?
                </h2>

                <form onSubmit={submitForm} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <Field label="Nome e Cognome *" error={errors.full_name}>
                      <input data-testid="org-input-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
                    </Field>
                  </div>
                  <Field label="Ruolo / Titolo *" error={errors.role}>
                    <input data-testid="org-input-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Azienda *" error={errors.company}>
                    <input data-testid="org-input-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Email aziendale *" error={errors.email}>
                      <input data-testid="org-input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Numero dipendenti *" error={errors.employees}>
                      <div className="grid grid-cols-4 gap-2">
                        {EMPLOYEE_OPTIONS.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setForm({ ...form, employees: opt })}
                            data-testid={`org-emp-${opt}`}
                            className={`px-2 py-3 text-sm font-semibold border transition-colors duration-300 ${
                              form.employees === opt ? "border-gold bg-gold/10 text-gold" : "border-white/15 bg-[#111] text-white/70 hover:border-gold/60"
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Telefono (facoltativo)">
                      <input data-testid="org-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="Numero di telefono" />
                    </Field>
                  </div>

                  <div className="sm:col-span-2 space-y-4">
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="org-checkbox-privacy"
                          checked={form.privacy_accepted}
                          onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })}
                          className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                        />
                        <span className="text-xs sm:text-[13px] text-gray-400 leading-relaxed">
                          Ho letto e accetto la{" "}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2 hover:text-gold-hover">
                            Privacy Policy
                          </a>. *
                        </span>
                      </label>
                      {errors.privacy && <p className="mt-2 text-xs text-red-400">{errors.privacy}</p>}
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="org-checkbox-marketing"
                        checked={form.marketing_accepted}
                        onChange={(e) => setForm({ ...form, marketing_accepted: e.target.checked })}
                        className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer"
                      />
                      <span className="text-xs sm:text-[13px] text-gray-400 leading-relaxed">
                        Acconsento a ricevere la newsletter e comunicazioni informative e promozionali di CC Elevate via email. <span className="text-white/40">(facoltativo)</span>
                      </span>
                    </label>
                  </div>

                  {apiError && <div className="sm:col-span-2 text-sm text-red-400" data-testid="org-form-error">{apiError}</div>}

                  <div className="sm:col-span-2 flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={() => setStep(total - 1)}
                      className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase text-white/60 hover:text-gold transition-colors duration-300"
                    >
                      <ArrowLeft className="h-4 w-4" /> Indietro
                    </button>
                    <GoldButton type="submit" disabled={submitting} testId="org-submit-btn">
                      {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Invio...</>) : (<>Scopri la diagnosi <ArrowRight className="h-4 w-4" /></>)}
                    </GoldButton>
                  </div>
                </form>
              </div>
            </motion.div>
          )}

          {/* RESULT */}
          {step === "result" && result && (
            <motion.div key="result" {...fade} className="flex-1" data-testid="org-result-page">
              <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-6 sm:px-12 py-12 sm:py-14">
                <Kicker>Diagnosi Organizzativa</Kicker>
                <div className="mt-5 flex items-baseline gap-4">
                  <span className="font-serif text-5xl sm:text-6xl text-gold" data-testid="org-score">{result.score}</span>
                  <span className="text-white/50 text-lg">/ 100</span>
                </div>
                <h1 className="mt-4 font-serif text-3xl sm:text-4xl text-white leading-tight" data-testid="org-result-title">
                  {ORG_RESULTS[result.band].title}
                </h1>
                <div className="mt-5 h-px w-full bg-gradient-to-r from-gold/60 via-gold/20 to-transparent" />
                <p className="mt-6 text-gray-300 text-sm sm:text-base leading-relaxed">
                  {ORG_RESULTS[result.band].body}
                </p>
                <div className="mt-8 border-l-2 border-gold/60 pl-4">
                  <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold">Consiglio Strategico</p>
                  <p className="mt-1.5 text-gray-300 text-sm sm:text-[15px] leading-relaxed">{ORG_RESULTS[result.band].advice}</p>
                </div>

                <div className="mt-9 flex items-center gap-2 text-sm" data-testid="org-email-status">
                  {result.email_sent ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-gold" />
                      <span className="text-gray-300">Ti abbiamo inviato la diagnosi completa via email.</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-white/40" />
                      <span className="text-gray-500">Diagnosi salvata. L'invio email non è al momento attivo.</span>
                    </>
                  )}
                </div>

                <div className="mt-8">
                  <button
                    type="button"
                    onClick={restart}
                    data-testid="org-restart-btn"
                    className="text-xs sm:text-sm font-bold tracking-[0.15em] uppercase text-gold hover:text-gold-hover transition-colors duration-300"
                  >
                    Rifai l'assessment
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Footer />
      </div>
    </div>
  );
}
